import { createStore } from "solid-js/store";
import { For, Match, Show, Switch, createSignal, onMount } from "solid-js";
import _ from "underscore";
import { A, useNavigate, useRouteData } from "@solidjs/router";
import { fetchPost, scrollToTop } from "../library/common";
import { ImageFileData } from "../components/universal/image-cropper";
import { EditArtworkData, EditArtworkModel, ListItem } from "../types";
import { useForm } from "../components/universal/validation";
import { Progress } from "../components/progress";
import { Button, Form } from "solid-bootstrap";
import { Required } from "../components/universal/required-icon";
import toast from "solid-toast";
import { getArtworksCountJson } from "../app";
import { CMS } from "../components/cms/cms";
import { ModelEditor, ModelImageCropper, ModelProvider, ModelTextBox } from "../components/model-form-elements";
import { PageLoading } from "../components/page-loading";
import { CharacterCount } from "../components/character-count";

type ArtistArtworkImageData = {
    IsPrimary: boolean;
    ArtworkId: number;
    NewImageId?: string;
    ArtworkImageId?: string;
    OriginalFilename: string;
    ImageFile: Blob;
};

type ImageFileDataListItem = {
    file: ImageFileData;
    order: number;
    id: string;
    artworkImageId: string;
    isPrimary: boolean;
};

type State = {
    isContentReady: boolean;
}

type ButtonActiveState = {
    submit: boolean;
}

export function EditArtwork() {
    const notify = {
        uploadingImages: (artwork: EditArtworkModel, imageFiles: ImageFileDataListItem[]) => {
            const numImages = imageFiles.length == 1 ? "image" : "images";
            return toast.loading(`Attaching your ${numImages} to "${artwork.Title}" ...`);
        },
        imageUploadError: (toastId: string, imageFile: ImageFileDataListItem) => {
            toast.error("[003] There was an error attaching an image to the application. The image file is: " + imageFile.file.filename, {
                id: toastId
            });
        },
        uploadedImages: (toastId: string, artwork: EditArtworkModel,imageFiles: ImageFileDataListItem[]) => {
            const numImages = imageFiles.length == 1 ? "image" : "images";
            const wasWere = imageFiles.length == 1 ? "was" : "were";
            toast.success(`${imageFiles.length} ${numImages} ${wasWere} uploaded to "${artwork.Title}".`, {
                id: toastId
            });
        },
        savedArtwork: () => toast.success(`Saved "${artwork.Title}".`),
        unitRequired: () => toast.error("You must select a unit of measurement."),
        commentRequired: () => toast.error("You must provide a comment on the artwork."),
        noImageSelected: () => toast.error("No images selected."),
        imageRequired: () => toast.error("[004] Please attach at least one image."),
        invalidImage: () => toast.error("[001] One of the image files is invalid; please check that the image format is either JPG or PNG."),
        unableToSaveImages: () => toast.error("[002] Unable to save the artwork details at this time.")
    };
    const url = {
        saveArtwork: "/LivingArtistApplication/SaveArtworkJson",
        saveArtworkImage: "/LivingArtistApplication/SaveArtworkImageJson"
    };
    const [state, setState] = createStore<State>({ isContentReady: false } as State);
    const [buttonActive, setButtonActive] = createStore<ButtonActiveState>({ submit: true } as ButtonActiveState);
    const { formSubmit, validate, errors, setErrors } = useForm({
        errorClass: "error-input"
    });  
    const navigate = useNavigate();
    const [artwork, setArtwork] = createStore<EditArtworkModel>({
        Title:"",
        UnitOfMeasurement: "",
        Medium:"",
        Height:"",
        Width:"",
        Depth:""
    } as EditArtworkModel);
    const [unitOfMeasurements, setUnitOfMeasurements] = createSignal<ListItem[]>([{ Text: "inches", Value: "ins" },{ Text: "cms", Value: "cms" }]);
    const getModel = () => {
        return {
            ...artwork,
            Description: artwork.DescriptionEditor?.getHtml()
        };
    };
    const requiresUnitOfMeasurement = () => !artwork.UnitOfMeasurement;
    const validateOtherFields = () => {
        var isValid = true;
        setErrors("UnitOfMeasurement", "");
        if (artwork.DescriptionEditor?.isEmpty()) {
            notify.commentRequired();
            isValid = false;
        }
        if (requiresUnitOfMeasurement()) {
            notify.unitRequired();
            isValid = false;
        }
        return isValid;
    };
    const submitIfValid = async () => {
        setButtonActive("submit", false);
        const isValid = validateOtherFields();
        if (!isValid) {
            return;
        }
        await save();
        await getArtworksCountJson({ forceRefresh: true });
        setButtonActive("submit", true);
    };
    const getImageFiles = async () => {
        const list = [];
        const image1 = await artwork.FirstImageCropper?.getImageFile();
        const image2 = await artwork.SecondImageCropper?.getImageFile();
        if (image1) list.push({...image1, artworkImageId: artwork.FirstImage?.Id + "", isPrimary: true });
        if (image2) list.push({...image2, artworkImageId: artwork.SecondImage?.Id + "", isPrimary: false });
        return list;
    }
    const toArtistArtworkImageDto = (artworkId: number, imageFile: ImageFileDataListItem) => {
        let model = {
            IsPrimary: imageFile.isPrimary,
            ArtworkId: artworkId,
            ArtworkImageId: imageFile.artworkImageId,
            NewImageId: imageFile.id,
            ImageFile: imageFile.file.image,
            OriginalFilename: imageFile.file.filename
        } as ArtistArtworkImageData;
        return model;
    };
    const saveImages = async (artworkId: number, imageFiles: ImageFileDataListItem[]) => {
        if (!imageFiles.length) {
            notify.noImageSelected();
            return navigate("/step-2");
        }
        const toastId = notify.uploadingImages(artwork, imageFiles);
        for await (let imageFile of imageFiles) {
            let model = toArtistArtworkImageDto(artworkId, imageFile);
            let response = await fetchPost(url.saveArtworkImage, model);
            let json = await response.json();
            if (!json.Success) {
                notify.imageUploadError(toastId, imageFile);
                return;
            }
        }
        notify.uploadedImages(toastId, artwork, imageFiles);
        navigate("/step-2");
    }
    const save = async () => {
        const model = getModel();
        const allImages = await getImageFiles();
        const newImages = allImages.filter(f => f.isDirty);
        if (!allImages.length) {
            return notify.imageRequired();
        }
        if (newImages.some(f => !f.isValid)) {
            return notify.invalidImage();
        }
        const res = await fetchPost(url.saveArtwork, model);
        const json = await res.json();
        if (!json.Success) {
            return notify.unableToSaveImages();
        }
        const id = json.Data.Id;
        setArtwork({ Id: id });
        notify.savedArtwork();
        const images: ImageFileDataListItem[] = newImages.map((file, i) => {
            const item: ImageFileDataListItem = {
                file,
                order: i + 1,
                artworkImageId: (file.artworkImageId || ""),
                id: (file.id || "") + "",
                isPrimary: file.isPrimary
            };
            return item;
        });
        await saveImages(id, images);
    };
    onMount(async () => {
        const routeData = await useRouteData() as EditArtworkData;
        const _artwork = routeData.artwork;
        setArtwork(_artwork as any);
        if (_artwork.FirstImage) {
            setArtwork("FirstImage", _artwork.FirstImage);
            setArtwork("FirstImageUrl", _artwork.FirstImage.NewImage.URL);
        }
        if (_artwork.SecondImage) {
            setArtwork("SecondImage", _artwork.SecondImage);
            setArtwork("SecondImageUrl", _artwork.SecondImage.NewImage.URL);
        }
        setState({ isContentReady: true });
        scrollToTop();
    });
    return (
        <>
            <Show when={state.isContentReady} fallback={<PageLoading />}>
                <ModelProvider model={artwork} setModel={setArtwork} validation={{ errors, validate }}>
                    <div>
                        <Progress step={2} />
                        <form use:formSubmit={submitIfValid}>
                            <div class="row">
                                <div class="col-md-6" style="padding-right: 20px;">
                                    <div class="form-group">
                                        <h4 class="m-t-xxl m-b-lg" style="font-family: 'Oswald'; font-size: 3em;">
                                            <CMS id="label::artwork-details">Artwork details</CMS>
                                            <Show when={!!artwork.Id}>
                                                <small># {artwork.Id}</small>
                                            </Show>
                                        </h4>
                                        <p>
                                            <CMS id="message::required-fields">
                                                Fields that are marked with a <span class="fa fa-star" style="color: red;"></span> are required.
                                            </CMS>
                                        </p>
                                        <div class="row">
                                            <div class="col-md-12">
                                                <ModelTextBox<EditArtworkModel>
                                                    field="Title"
                                                    required
                                                />
                                                <p>
                                                    <CharacterCount value={artwork.Title} max={255} />
                                                </p>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-md-12">
                                                <ModelTextBox<EditArtworkModel>
                                                    field="Medium"
                                                    required
                                                />
                                                <p>
                                                    <CharacterCount value={artwork.Medium} max={60} />
                                                </p>
                                            </div>
                                        </div>
                                        <hr/>
                                        <div class="row" style="margin-top: 40px;">
                                            <div class="col-md-12">
                                                <label>
                                                    <span class="fa fa-star" style="color: red;"></span>
                                                    <CMS id="label::unit-of-measurement" text>Unit of measurement</CMS>
                                                </label>
                                                <div>
                                                    <div class="btn-group" data-toggle="buttons">
                                                        <For each={unitOfMeasurements()}>{(unit: ListItem, index) =>
                                                            <Form.Check
                                                                inline
                                                                label={unit.Text}
                                                                value={unit.Value}
                                                                name="UnitOfMeasurement"
                                                                checked={artwork.UnitOfMeasurement === unit.Value}
                                                                onChange={(e) => setArtwork("UnitOfMeasurement", e.currentTarget.value)}
                                                                type="radio"
                                                                id={`unit-of-measurement-` + index()}
                                                            />
                                                        }</For>
                                                    </div>
                                                    <Show when={requiresUnitOfMeasurement()}>
                                                        <div style="color: red;">
                                                            <CMS id="error::unit-of-measurement/please-select-a-unit">Please select a unit of measurement</CMS>
                                                        </div>
                                                    </Show>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row" style="margin-top: 20px;">
                                            <div class="col-md-4">
                                                <ModelTextBox<EditArtworkModel>
                                                    field="Height"
                                                    required
                                                />
                                            </div>
                                            <div class="col-md-4">
                                                <ModelTextBox<EditArtworkModel>
                                                    field="Width"
                                                    required
                                                />
                                            </div>
                                            <div class="col-md-4">
                                                <ModelTextBox<EditArtworkModel>
                                                    field="Depth"
                                                />
                                            </div>
                                        </div>
                                        <div class="row" style="margin-top: 20px;">
                                            <div class="col-md-12">
                                                <ModelEditor<EditArtworkModel>
                                                    field="Description" editor="DescriptionEditor"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6" style="padding-left: 20px;">
                                    <h4 class="salon-heading-md m-t-xxl m-b-lg">
                                        <CMS id="salon-application::heading/upload-artwork-images" text>Upload Artwork Images</CMS>
                                    </h4>
                                    <CMS id="scholarship-application/edit-artwork::images/instructions">
                                        <h4>
                                            Images
                                        </h4>
                                        <p>
                                            Images should be in either JPEG or PNG format and should be between 1MB and 10MB in size, each.
                                        </p>
                                    </CMS>
                                    <div>
                                        <div style="border: 1px solid #ccc; padding: 30px; margin: 0px; margin-top: 25px; border-radius: 10px;">
                                            <div>
                                                <label style="font-size: 1.5em;">
                                                    <Required /> <CMS id="label::image" text>Image #1</CMS>
                                                </label>
                                                <ModelImageCropper<EditArtworkModel>
                                                    imageUrl="FirstImageUrl"
                                                    editor="FirstImageCropper"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div style="border: 1px solid #ccc; padding: 30px; margin: 0px; margin-top: 25px; border-radius: 10px;">
                                            <div>
                                                <label style="font-size: 1.5em;">
                                                    <CMS id="label::image" text>Image #2</CMS>
                                                </label>
                                                <ModelImageCropper<EditArtworkModel>
                                                    imageUrl="SecondImageUrl"
                                                    editor="SecondImageCropper"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Show when={artwork.Id == undefined}>
                                <div class="row">
                                    <div class="col-md-6">
                                        <p>
                                            <CMS id="salon-application/edit-artwork::message/please-be-patient">
                                                <strong>Note:</strong> After you press the button to submit it may take quite a while for your files to upload, please be patient!
                                            </CMS>
                                        </p>
                                    </div>
                                </div>
                            </Show>
                            <div class="row m-t-xxl">
                                <div class="col-md-12">
                                    <div id="submit-row">
                                        <A type="button" class="btn btn-default" href={"/step-2"}><CMS id="button::cancel">Cancel</CMS></A>
                                        <Button type="submit" variant="success" classList={{ ["disable"] : !buttonActive.submit }} style="margin-right: 20px;">
                                            <Switch>
                                                <Match when={!!artwork.Id}><CMS id="button::save-changes">Save Changes</CMS></Match>
                                                <Match when={!artwork.Id}><CMS id="button::submit-this-artwork">Submit this Artwork</CMS></Match>
                                            </Switch>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </ModelProvider>
            </Show>
        </>
    );
}